import { useCallback } from 'react'
import { useStore } from '../store/index.js'
import { usePlayoutWebSocket } from './useWebSocket.js'
import type { WsMessage, ItemStatus } from '../types/index.js'

/** Applique un message reçu à l'état local. Exporté pour figer le contrat
 * temps réel dans les tests sans devoir simuler un navigateur complet. */
export function applyRealtimeMessage(channelId: string, msg: WsMessage) {
  const {
    setWsStateReady,
    setPlayoutState,
    patchPlayoutState,
    addAlert,
    updateItemStatus: updateItemStatusInSchedule,
    incrementGridUpdateSeq,
    incrementGridsUpdateSeq,
    incrementProgramsUpdateSeq,
    setActiveBugType,
    setLiveEndingAlert,
  } = useStore.getState()
  switch (msg.type) {

      // ── État initial envoyé à la connexion ────────────────────────────────
      case 'STATE_INIT':
        setPlayoutState(msg.data)
        setWsStateReady(channelId)
        // Une reconnexion est aussi un point de resynchronisation REST : les
        // vues remontées doivent repartir des grilles réellement présentes.
        incrementGridUpdateSeq()
        if ('activeBugType' in msg.data) {
          setActiveBugType((msg.data as any).activeBugType ?? null)
        }
        break

      // ── Mise à jour partielle du playout (frameCount, état…) ──────────────
      case 'PLAYOUT_UPDATE':
        if ('activeBugType' in msg.data) {
          setActiveBugType((msg.data as any).activeBugType ?? null)
        }
        if ('currentItemId' in msg.data) {
          const previousItemId = useStore.getState().playoutState?.currentItemId ?? null
          const nextItemId = (msg.data as any).currentItemId ?? null
          if (previousItemId && previousItemId !== nextItemId) {
            updateItemStatusInSchedule(previousItemId, 'DONE')
          }
          if (nextItemId && previousItemId !== nextItemId) {
            updateItemStatusInSchedule(nextItemId, 'ON_AIR')
          }
        }
        patchPlayoutState(msg.data)
        break

      // ── Alerte watchdog (dérive, freeze, silence, signal lost…) ──────────
      case 'WATCHDOG_ALERT':
        addAlert(msg.data)
        break

      // ── Changement de statut d'un item (SCHEDULED → ON_AIR → DONE…) ──────
      case 'ITEM_STATUS': {
        // Un statut qui vise un élément encore absent des listes n'est pas un
        // bruit : c'est un élément créé à l'instant (reprise de raccrochage,
        // comblage) dont le statut a doublé le rechargement déclenché par
        // `GRID_UPDATED`. L'ignorer figeait le chrono de partie jusqu'au
        // rafraîchissement de la page ; on relance donc le rechargement.
        const { items: dayItems, liveItems } = useStore.getState()
        const known = dayItems.some(item => item.id === msg.itemId)
          || liveItems.some(item => item.id === msg.itemId)
        updateItemStatusInSchedule(msg.itemId, msg.status as ItemStatus)
        if (!known) incrementGridUpdateSeq()
        // Alerte "début de clip" quand un item passe ON_AIR
        if (msg.status === 'ON_AIR') {
          const item = useStore.getState().items.find(i => i.id === msg.itemId)
          const title = item?.program?.title ?? item?.liveSource?.name ?? item?.interludeClip?.name ?? 'Clip'
          addAlert({
            type:     'CLIP_STARTED',
            severity: 'OK',
            itemId:   msg.itemId,
            message:  `▶ ${title} — début de diffusion`,
            ts:       new Date().toISOString(),
          })
        }
        break
      }

      // ── Grille modifiée (skip, recalage, autre client…) ─────────────────
      case 'GRID_UPDATED':
        // Incrémente le compteur → useAgendaItems se re-déclenche automatiquement
        incrementGridUpdateSeq()
        break

      // ── Chevauchement laissé par un réalignement automatique ────────────
      // Un média plus long que son créneau fait déborder le bloc sur le
      // programme suivant. La barre de chevauchements de la grille, calculée
      // depuis la grille elle-même, porte le recalage ; ici on n'en garde
      // qu'une trace, sans carte d'action qui resterait à l'écran une fois le
      // chevauchement réglé. « Voir » ramène sur l'élément qui déborde.
      case 'GRID_OVERLAP': {
        useStore.getState().pushToast(
          `Chevauchement : ${msg.message}`,
          'error',
          undefined,
          undefined,
          { focus: { itemId: msg.overItemId, iso: msg.startTime, gridId: msg.gridId } },
        )
        break
      }

      // ── Liste de grilles modifiée (création, activation, archivage…) ────
      case 'GRIDS_UPDATED':
        incrementGridsUpdateSeq()
        break

      // ── Catalogue programmes modifié ─────────────────────────────────────
      case 'PROGRAMS_UPDATED':
        incrementProgramsUpdateSeq()
        break

      case 'LOGO_PROFILES_UPDATED':
        window.dispatchEvent(new CustomEvent('trafficplay:logo-profiles-updated', { detail: msg.channelId }))
        break

      case 'PLAYOUT_TARGETS_UPDATE':
        patchPlayoutState(msg.data)
        break

      case 'PLAYOUT_FAILOVER':
        patchPlayoutState({
          activeTargetId: typeof msg.data.toTargetId === 'string' ? msg.data.toTargetId : null,
          activeTargetName: typeof msg.data.toTargetName === 'string' ? msg.data.toTargetName : null,
        })
        break

      case 'LIVE_ENDING_SOON':
        setLiveEndingAlert({ itemId: msg.itemId, sourceName: msg.sourceName, endsInSec: msg.endsInSec })
        break

      case 'ERROR':
        console.error('[WS] Erreur serveur:', msg.message)
        addAlert({
          type:     'LOAD_FAILURE',
          severity: 'ERROR',
          message:  `WebSocket : ${msg.message}`,
          ts:       new Date().toISOString(),
        })
        break

      case 'ACK':
      case 'PING':
        // Silencieux
        break
  }
}

export function usePlayoutSync(channelId: string | null) {
  const onMessage = useCallback((msg: WsMessage) => {
    applyRealtimeMessage(channelId ?? '', msg)
  }, [channelId])

  const onConnect = useCallback(() => {
    useStore.getState().setWsConnected(true)
  }, [])

  const onDisconnect = useCallback(() => {
    useStore.getState().setWsConnected(false)
  }, [])

  // Une session refusée n'est pas une coupure réseau : la reconnexion aveugle
  // laissait alors l'opérateur devant « Reconnexion au serveur… » indéfiniment,
  // alors qu'il fallait simplement se réidentifier. Le même événement que le
  // 401 HTTP ramène l'écran d'identification.
  const onClose = useCallback((code: number) => {
    if (code === 4401) window.dispatchEvent(new CustomEvent('trafficplay:auth-required'))
  }, [])

  const { send, reconnect } = usePlayoutWebSocket({
    channelId:    channelId ?? '',
    onMessage,
    onConnect,
    onDisconnect,
    onClose,
    enabled:      !!channelId,
  })

  // `reconnect` court-circuite la temporisation en cours : de quoi câbler une
  // reprise manuelle sans attendre le prochain essai automatique.
  return { send, reconnect }
}
